import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthUser {
  id: string;
  name: string;
  email?: string;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  role: string | null;
  loading: boolean;
  login: (tokenData: string, userData: AuthUser, userRole: string) => void;
  logout: () => void;
  isAdmin: boolean;
  isTherapist: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    const storedRole = localStorage.getItem('auth_role');
    
    if (storedToken && storedUser && storedRole) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setRole(storedRole);
    }
    setLoading(false);
  }, []);

  const login = (tokenData: string, userData: AuthUser, userRole: string) => {
    localStorage.setItem('auth_token', tokenData);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    localStorage.setItem('auth_role', userRole);
    setToken(tokenData);
    setUser(userData);
    setRole(userRole);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_role');
    setToken(null);
    setUser(null);
    setRole(null);
  };

  const value: AuthContextValue = {
    user,
    token,
    role,
    loading,
    login,
    logout,
    isAdmin: role === 'admin',
    isTherapist: role === 'therapist',
    isAuthenticated: !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
